// src/components/operations/property_setup/index.js
import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import UIkit from "uikit";
import { AppContexts } from "../../../providers";
import Tags from "../../common/Tags";
import standardsLibrary from "../standards/ruebarue_standards_library.json";

const LS_STANDARDS_KEY = "ruebarue_org_standards_v1";
const LS_PROPERTY_OVERRIDES_KEY = "ruebarue_property_overrides_v1";

const safeArray = (v) => (Array.isArray(v) ? v : []);

const entityKey = (x) =>
  x?.key || x?.id || x?.value || x?.rental_id || x?.pms_id || x?.property_id || null;

const titleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const labelForProperty = (p) => {
  const name = p?.name || p?.title || p?.nickname || p?.pms_id || p?.property_id || p?.id;
  return titleCase(name);
};

const buildOrgStandardsFromLibrary = () => ({
  version: standardsLibrary.version,
  library_name: standardsLibrary.library_name,
  task_types: standardsLibrary.defaults?.task_types || [],
  field_sets: standardsLibrary.defaults?.field_sets || [],
  space_groups: standardsLibrary.defaults?.space_groups || [],
  space_types: standardsLibrary.defaults?.space_types || [],
  item_types: standardsLibrary.defaults?.item_types || [],
  checklist_templates: standardsLibrary.defaults?.checklist_templates || [],
});

const computeAppliedItemsForSpace = ({ orgStandards, spaceType }) => {
  const itemTypeByKey = Object.fromEntries(
    safeArray(orgStandards?.item_types).map((it) => [it.key, it])
  );

  return safeArray(spaceType?.default_item_type_keys)
    .map((k) => itemTypeByKey[k])
    .filter(Boolean)
    .sort((a, b) => String(a?.label || a?.key).localeCompare(String(b?.label || b?.key)));
};

const loadOrgStandards = () => {
  try {
    const raw = localStorage.getItem(LS_STANDARDS_KEY);
    if (!raw) return buildOrgStandardsFromLibrary();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return buildOrgStandardsFromLibrary();
    return parsed;
  } catch {
    return buildOrgStandardsFromLibrary();
  }
};

const loadOverrides = () => {
  try {
    const raw = localStorage.getItem(LS_PROPERTY_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const saveOverrides = (next) => {
  try {
    localStorage.setItem(LS_PROPERTY_OVERRIDES_KEY, JSON.stringify(next));
  } catch (e) {
    console.warn("Could not save overrides", e);
  }
};

const stop = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const stopAll = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

// -----------------------------
// Dummy data for demo fallback
// -----------------------------
const DUMMY_PROPERTIES = [
  {
    rental_id: "demo_101",
    pms_id: "RB-101",
    name: "Oceanview Cottage",
    address1: "123 Surf Ave",
    city: "Cannon Beach",
    state: "OR",
    bedrooms: 3,
    bathrooms: 2,
    amenities: ["hot_tub", "deck_patio", "fireplace", "washer_dryer"],
    tags: ["family", "ocean_view"],
  },
  {
    rental_id: "demo_102",
    pms_id: "RB-102",
    name: "Downtown Loft",
    address1: "88 Main St",
    city: "Portland",
    state: "OR",
    bedrooms: 1,
    bathrooms: 1,
    amenities: ["garage", "office", "laundry"],
    tags: ["urban", "long_stays"],
  },
  {
    rental_id: "demo_103",
    pms_id: "RB-103",
    name: "Mountain Cabin",
    address1: "45 Pine Ridge Rd",
    city: "Bend",
    state: "OR",
    bedrooms: 4,
    bathrooms: 3,
    amenities: ["hot_tub", "yard", "game_room", "deck_patio"],
    tags: ["ski", "groups"],
  },
];

// Minimal “applied spaces” rule for demo (amenities + tags)
const computeAppliedSpaces = ({ orgStandards, property }) => {
  const pAmenities = new Set(
    safeArray(property?.amenities)
      .map((a) => (typeof a === "string" ? a : a?.key || a?.id || a?.value))
      .filter(Boolean)
  );

  const pTags = new Set(
    safeArray(property?.ops_tags || property?.tags)
      .map((t) => String(t).trim())
      .filter(Boolean)
  );

  const spaces = safeArray(orgStandards?.space_types).filter((st) => {
    const amenityKeys = safeArray(st?.applies_to_amenity_keys);
    const tagKeys = safeArray(st?.applies_to_tags);

    // If neither is defined, treat as “general”
    if (!amenityKeys.length && !tagKeys.length) return true;

    const amenityHit = amenityKeys.some((k) => pAmenities.has(k));
    const tagHit = tagKeys.some((t) => pTags.has(t));

    return amenityHit || tagHit;
  });

  return spaces.sort((a, b) =>
    String(a?.label || a?.key || "").localeCompare(String(b?.label || b?.key || ""))
  );
};

const PropertySetup = () => {
  const settingsContext = useContext(AppContexts.SettingsContext) || {};
  const rentalsContext = useContext(AppContexts.RentalsContext) || {};
  const { user, refresh } = useContext(AppContexts.AuthContext) || {};

  const [orgStandards] = useState(() => loadOrgStandards());

  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [propertyOverridesById, setPropertyOverridesById] = useState(() => loadOverrides());

  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "Property Setup - RueBaRue";
  }, []);

  // Try contexts first; if empty, fall back to dummy properties
  const ctxProperties = useMemo(() => {
    const candidates = [
      settingsContext?.rentals,
      settingsContext?.properties,
      settingsContext?.units,
      rentalsContext?.rentals,
      rentalsContext?.properties,
      rentalsContext?.units,
      rentalsContext?.data,
      rentalsContext?.list,
      settingsContext?.data,
      settingsContext?.list,
    ];

    for (const arr of candidates) {
      if (Array.isArray(arr) && arr.length) return arr;
    }
    return [];
  }, [settingsContext, rentalsContext]);

  useEffect(() => {
    if (ctxProperties.length) {
      setProperties(ctxProperties);
      return;
    }
    // ✅ fallback for demo
    setProperties(DUMMY_PROPERTIES);
  }, [ctxProperties]);

  useEffect(() => {
    if (!selectedPropertyId && properties.length) {
      setSelectedPropertyId(String(entityKey(properties[0])));
    }
  }, [properties, selectedPropertyId]);

  const filteredProperties = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return properties;

    return properties.filter((p) => {
      const hay = [
        p?.name,
        p?.title,
        p?.nickname,
        p?.pms_id,
        p?.property_id,
        p?.address,
        p?.address1,
        p?.city,
        p?.state,
        p?.postal_code,
        p?.id,
        p?.rental_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(query);
    });
  }, [properties, q]);

  const selectedProperty = useMemo(() => {
    if (!selectedPropertyId) return null;
    return properties.find((p) => String(entityKey(p)) === String(selectedPropertyId)) || null;
  }, [properties, selectedPropertyId]);

  // ✅ 1) currentOverride FIRST
  const currentOverride = useMemo(() => {
    if (!selectedPropertyId) return null;
    return (
      propertyOverridesById[selectedPropertyId] || {
        ops_tags: [],
        disabled_space_keys: [],
        disabled_item_keys: [],
        item_check_overrides: {},
        item_task_overrides: {},
      }
    );
  }, [propertyOverridesById, selectedPropertyId]);

  // ✅ 2) persistOverride SECOND
  const persistOverride = useCallback(
    (patch, toastMsg = "Saved.") => {
      if (!selectedPropertyId) return;

      setPropertyOverridesById((prev) => {
        const next = { ...prev };
        const base =
          next[selectedPropertyId] || {
            ops_tags: [],
            disabled_space_keys: [],
            disabled_item_keys: [],
            item_check_overrides: {},
            item_task_overrides: {},
          };

        next[selectedPropertyId] = { ...base, ...patch };
        saveOverrides(next);
        return next;
      });

      if (toastMsg) UIkit.notification({ message: toastMsg, status: "success" });
    },
    [selectedPropertyId]
  );

  // ✅ 3) toggles AFTER both exist
  const toggleDisableSpace = useCallback(
    (spaceKey) => {
      const cur = safeArray(currentOverride?.disabled_space_keys);
      const set = new Set(cur);
      if (set.has(spaceKey)) set.delete(spaceKey);
      else set.add(spaceKey);
      persistOverride({ disabled_space_keys: Array.from(set) }, "Override saved.");
    },
    [currentOverride, persistOverride]
  );

  const toggleDisableItem = useCallback(
    (itemKey) => {
      const cur = safeArray(currentOverride?.disabled_item_keys);
      const set = new Set(cur);
      if (set.has(itemKey)) set.delete(itemKey);
      else set.add(itemKey);
      persistOverride({ disabled_item_keys: Array.from(set) }, "Override saved.");
    },
    [currentOverride, persistOverride]
  );

  // ✅ Tags ONLY on column 2 (exact style requested)
  const onChangeTags = useCallback(
    (nextTags) => {
      const cleaned = Array.from(
        new Set(safeArray(nextTags).map((t) => String(t).trim()).filter(Boolean))
      );
      persistOverride({ ops_tags: cleaned }, "Tags saved.");
    },
    [persistOverride]
  );

  const setItemDisabled = useCallback(
    (itemKey, disabled) => {
      const cur = safeArray(currentOverride?.disabled_item_keys);
      const set = new Set(cur);

      if (disabled) set.add(itemKey);
      else set.delete(itemKey);

      persistOverride({ disabled_item_keys: Array.from(set) }, "Item override saved.");
    },
    [currentOverride, persistOverride]
  );

  const overrideKeyFor = (spaceKey, itemKey) => `${spaceKey}:${itemKey}`;

  const isCheckDisabled = useCallback(
    (spaceKey, itemKey, checkKey) => {
      const k = overrideKeyFor(spaceKey, itemKey);
      return !!currentOverride?.item_check_overrides?.[k]?.[checkKey]?.disabled;
    },
    [currentOverride]
  );

  const setCheckDisabled = useCallback(
    (spaceKey, itemKey, checkKey, disabled) => {
      const k = overrideKeyFor(spaceKey, itemKey);
      const cur = currentOverride?.item_check_overrides || {};
      const curNode = cur[k] || {};
      const nextNode = { ...curNode, [checkKey]: { ...(curNode[checkKey] || {}), disabled } };
      const next = { ...cur, [k]: nextNode };
      persistOverride({ item_check_overrides: next }, "Checklist override saved.");
    },
    [currentOverride, persistOverride]
  );  

  const isTaskDisabled = useCallback(
    (spaceKey, itemKey, taskKey) => {
      const k = overrideKeyFor(spaceKey, itemKey);
      return !!currentOverride?.item_task_overrides?.[k]?.[taskKey]?.disabled;
    },
    [currentOverride]
  );

  const setTaskDisabled = useCallback(
    (spaceKey, itemKey, taskKey, disabled) => {
      const k = overrideKeyFor(spaceKey, itemKey);
      const cur = currentOverride?.item_task_overrides || {};
      const curNode = cur[k] || {};
      const nextNode = { ...curNode, [taskKey]: { ...(curNode[taskKey] || {}), disabled } };
      const next = { ...cur, [k]: nextNode };
      persistOverride({ item_task_overrides: next }, "Task override saved.");
    },
    [currentOverride, persistOverride]
  );

  const appliedSpaces = useMemo(() => {
    if (!selectedProperty) return [];
    return computeAppliedSpaces({
      orgStandards,
      property: { ...selectedProperty, ops_tags: currentOverride?.ops_tags },
    });
  }, [orgStandards, selectedProperty, currentOverride]);

  const addPropertyOverride = useCallback(() => {
    UIkit.notification({
      message: "Next: wire Spaces → Items → Checks preview + per-item overrides.",
      status: "primary",
    });
  }, []);

  const beds = selectedProperty?.bedrooms ?? selectedProperty?.beds ?? "";
  const baths = selectedProperty?.bathrooms ?? selectedProperty?.baths ?? "";

  const propertyId =
    selectedProperty?.pms_id ||
    selectedProperty?.property_id ||
    selectedProperty?.rental_id ||
    selectedProperty?.id;

  const addressLine =
    selectedProperty?.address ||
    [selectedProperty?.address1, selectedProperty?.city, selectedProperty?.state]
      .filter(Boolean)
      .join(", ");

  return (
    <section>
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <div>
          <h1 className="uk-heading-small">Property Setup</h1>
        </div>
      </div>

      <div id="hms-main-body">
        <div className="uk-grid-small" uk-grid="true">
          {/* ---------------- Column 1: Properties ---------------- */}
          <div className="uk-width-1-4@l uk-width-1-3@m uk-width-1-1">
            <div className="uk-card uk-card-default uk-card-small">
              <div className="uk-card-header uk-padding-small">
                <div className="uk-text-bold">Properties</div>
                <div className="uk-text-meta">Select a property</div>

                <div className="uk-margin-small-top">
                  <input
                    className="uk-input uk-form-small"
                    placeholder="Search…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>

              <div className="uk-card-body uk-padding-small">
                {filteredProperties.length === 0 ? (
                  <div className="uk-text-meta uk-padding-small">No properties found.</div>
                ) : (
                  <ul className="uk-list uk-list-divider">
                    {filteredProperties.map((p, idx) => {
                      const pk = String(entityKey(p) || idx);
                      const active = pk === String(selectedPropertyId);

                      return (
                        <li key={pk}>
                          <button
                            type="button"
                            onClick={() => setSelectedPropertyId(pk)}
                            className={`uk-button uk-button-text uk-text-left ${
                              active ? "hms-selected-row" : ""
                            }`}
                            style={{
                              width: "100%",
                              padding: "8px 6px 8px 12px",
                              borderRadius: 6,
                            }}
                          >
                            <div className="uk-flex uk-flex-column">
                              <div className="uk-text-bold">
                                {labelForProperty(p)}
                              </div>

                              <div className="uk-text-meta">
                                {p?.pms_id ? `ID: ${p.pms_id}` : ""}
                                {p?.pms_id && (p?.city || p?.state) ? " • " : ""}
                                {[p?.city, p?.state].filter(Boolean).join(", ")}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* ---------------- Column 2: Operations Setup + TAGS ONLY HERE ---------------- */}
          <div className="uk-width-1-3@l uk-width-1-3@m uk-width-1-1">
            <div className="uk-card uk-card-default uk-card-small">
              <div className="uk-card-header uk-padding-small">
                <div className="uk-text-bold">Operations Setup</div>
                <div className="uk-text-meta">
                  {selectedProperty ? "Amenities + tags determine applied standards" : "Select a property"}
                </div>
              </div>

              <div className="uk-card-body uk-padding-small">
                {!selectedProperty ? (
                  <div className="uk-text-meta uk-padding-small">Pick a property.</div>
                  ) : (
                  <>
                    <div className="uk-margin-small">
                      <div className="uk-text-bold">{labelForProperty(selectedProperty)}</div>

                      <div className="uk-text-meta uk-margin-xsmall-top">
                        {propertyId ? (
                          <>
                            ID: <span className="uk-text-bold">{propertyId}</span>
                          </>
                        ) : null}
                        {propertyId && (beds !== "" || baths !== "") ? " • " : null}
                        {beds !== "" ? (
                          <>
                            Beds: <span className="uk-text-bold">{beds}</span>
                          </>
                        ) : null}
                        {beds !== "" && baths !== "" ? " • " : null}
                        {baths !== "" ? (
                          <>
                            Baths: <span className="uk-text-bold">{baths}</span>
                          </>
                        ) : null}
                      </div>

                      {addressLine ? <div className="uk-text-meta">{addressLine}</div> : null}
                    </div>

                    {/* ✅ TAGS UI (exact pattern) */}
                    <div className="hms-add-tag uk-margin">
                      <div className="uk-form-label">Tags</div>
                      <div className="uk-flex uk-flex-top">
                        <Tags value={safeArray(currentOverride?.ops_tags)} onChange={onChangeTags} />
                      </div>
                    </div>

                    <hr />

                    <div className="uk-flex uk-flex-between uk-flex-middle">
                      <div className="uk-text-bold">Applied Spaces</div>
                      <div className="uk-text-meta">{appliedSpaces.length} total</div>
                    </div>

                    {appliedSpaces.length === 0 ? (
                      <div className="uk-text-meta uk-padding-small">
                        No spaces matched yet. Add tag/amenity mappings in standards.
                      </div>
                    ) : (
                      <ul uk-accordion="multiple: true" className="uk-accordion uk-margin-small-top">
                        {appliedSpaces.map((st) => {
                          const items = computeAppliedItemsForSpace({ orgStandards, spaceType: st });
                          const spaceDisabled = safeArray(currentOverride?.disabled_space_keys).includes(st.key);

                          return (
                            <li key={st.key} className={spaceDisabled ? "uk-opacity-60" : ""}>
                              {/* TITLE */}
                              <a className="uk-accordion-title" href="#!">
                                <div className="uk-flex uk-flex-between uk-flex-middle">
                                  <div className="uk-flex uk-flex-middle">
                                    <span className="uk-text-bold">{st.label || titleCase(st.key)}</span>
                                    <span className="uk-text-meta uk-margin-small-left">{items.length} items</span>
                                    {spaceDisabled ? <span className="uk-label uk-margin-small-left">Disabled</span> : null}
                                  </div>

                                  {/* SPACE TOGGLE */}
                                  <div
                                    onClick={stopAll}
                                    onMouseDown={stopAll}
                                    onPointerDown={stopAll}
                                    style={{ display: "flex", alignItems: "center" }}
                                  >
                                    <label className="setting-switch">
                                      <input
                                        type="checkbox"
                                        checked={!spaceDisabled}
                                        onClick={stopAll}
                                        onMouseDown={stopAll}
                                        onPointerDown={stopAll}
                                        onChange={() => toggleDisableSpace(st.key)}
                                      />
                                      <span className="setting-slider setting-round"></span>
                                    </label>
                                  </div>
                                </div>
                              </a>

                              {/* ✅ CONTENT — items/tasks/checks MUST be here */}
                              <div className="uk-accordion-content">
                                {items.length === 0 ? (
                                  <div className="uk-text-meta uk-padding-small">No items attached to this space.</div>
                                ) : (
                                  <ul className="uk-list uk-list-divider">
                                    {items.map((it) => {
                                      const itemDisabled = safeArray(currentOverride?.disabled_item_keys).includes(it.key);
                                      const tasks = safeArray(it.default_tasks);
                                      const checks = safeArray(it.checks);

                                      return (
                                        <li key={it.key} className={itemDisabled ? "uk-opacity-60" : ""}>
                                          <div className="uk-flex uk-flex-between uk-flex-middle">
                                            <div>
                                              <div className="uk-text-bold">
                                                {it.label || titleCase(it.key)}
                                                {itemDisabled ? <span className="uk-label uk-margin-small-left">Disabled</span> : null}
                                              </div>
                                              <div className="uk-text-meta">
                                                {tasks.length} tasks • {checks.length} checklist items
                                              </div>
                                            </div>

                                            {/* ITEM TOGGLE */}
                                            <div>
                                              <label className="setting-switch">
                                                <input
                                                  type="checkbox"
                                                  checked={!itemDisabled}
                                                  onChange={(e) => setItemDisabled(it.key, !e.target.checked)}
                                                />
                                                <span className="setting-slider setting-round"></span>
                                              </label>
                                            </div>
                                          </div>

                                          {/* TASKS + TOGGLES */}
                                          {tasks.length > 0 && (
                                            <div className="uk-margin-small-top">
                                              <div className="uk-text-small uk-text-bold">Tasks</div>
                                              <ul className="uk-list uk-list-small">
                                                {tasks.map((t) => {
                                                  const tKey = t.key || `${t.task_type_key}:${t.label}`;
                                                  const taskDisabled = isTaskDisabled(st.key, it.key, tKey);

                                                  return (
                                                    <li key={tKey} className={taskDisabled ? "uk-opacity-60" : ""}>
                                                      <div className="uk-flex uk-flex-between uk-flex-middle">
                                                        <div>
                                                          {t.label}
                                                          <span className="uk-label uk-label-default uk-margin-small-left">
                                                            {t.task_type_key}
                                                          </span>
                                                          {taskDisabled ? <span className="uk-label uk-margin-small-left">Disabled</span> : null}
                                                        </div>

                                                        <div>
                                                          <label className="setting-switch">
                                                            <input
                                                              type="checkbox"
                                                              checked={!taskDisabled}
                                                              onChange={(e) => setTaskDisabled(st.key, it.key, tKey, !e.target.checked)}
                                                            />
                                                            <span className="setting-slider setting-round"></span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            </div>
                                          )}

                                          {/* CHECKLIST + TOGGLES */}
                                          {checks.length > 0 && (
                                            <div className="uk-margin-small-top">
                                              <div className="uk-text-small uk-text-bold">Checklist Items</div>
                                              <ul className="uk-list uk-list-small">
                                                {checks.map((c) => {
                                                  const checkDisabled = isCheckDisabled(st.key, it.key, c.key);

                                                  return (
                                                    <li key={c.key} className={checkDisabled ? "uk-opacity-60" : ""}>
                                                      <div className="uk-flex uk-flex-between uk-flex-middle">
                                                        <div>
                                                          {c.label}
                                                          <span className="uk-label uk-label-default uk-margin-small-left">{c.type}</span>
                                                          {checkDisabled ? <span className="uk-label uk-margin-small-left">Disabled</span> : null}
                                                        </div>

                                                        <div>
                                                          <label className="setting-switch">
                                                            <input
                                                              type="checkbox"
                                                              checked={!checkDisabled}
                                                              onChange={(e) => setCheckDisabled(st.key, it.key, c.key, !e.target.checked)}
                                                            />
                                                            <span className="setting-slider setting-round"></span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            </div>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ---------------- Column 3: Overrides ---------------- */}
          <div className="uk-width-5-12@l uk-width-1-3@m uk-width-1-1">
            <div className="uk-card uk-card-default uk-card-small">
              <div className="uk-card-header uk-padding-small uk-flex uk-flex-between uk-flex-middle">
                <div>
                  <div className="uk-text-bold">Overrides</div>
                  <div className="uk-text-meta">Property-specific adjustments</div>
                </div>

                <button
                  type="button"
                  className="uk-button uk-button-small hms-btn hms-gray-btn"
                  disabled={!selectedProperty}
                  onClick={addPropertyOverride}
                >
                  + Override
                </button>
              </div>

              <div className="uk-card-body uk-padding-small">
                {!selectedProperty ? (
                  <div className="uk-text-meta uk-padding-small">Select a property to edit overrides.</div>
                ) : (
                  <>
                    <div className="uk-text-meta uk-margin-small-bottom">
                      Disabled spaces: {safeArray(currentOverride?.disabled_space_keys).length}
                    </div>

                    {safeArray(currentOverride?.disabled_space_keys).length === 0 ? (
                      <div className="uk-text-meta uk-padding-small">
                        No overrides yet. For the demo: disable a space from column 2.
                      </div>
                    ) : (
                      <ul className="uk-list uk-list-divider">
                        {safeArray(currentOverride?.disabled_space_keys).map((sk) => (
                          <li key={sk}>
                            <div className="uk-flex uk-flex-between uk-flex-middle">
                              <div>
                                <div className="uk-text-bold">{titleCase(String(sk).replace(/_/g, " "))}</div>
                                <div className="uk-text-meta">Space disabled for this property</div>
                              </div>

                              <button
                                type="button"
                                className="uk-button uk-button-text"
                                onClick={() => toggleDisableSpace(sk)}
                              >
                                Undo
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertySetup;